import { getDanhyPlatform } from "./utils";

let fallbackTimer = null;
let visibilityHandler = null;
let pageHideHandler = null;
let appOpened = false;

function getDanhyStoreUrl() {
  const ios = String(HM_Variables?.danhy_mobile_store_ios || "").trim();
  const android = String(HM_Variables?.danhy_mobile_store_android || "").trim();
  const platform = getDanhyPlatform();

  if (platform === "ios") {
    return ios || android;
  }
  if (platform === "android") {
    return android || ios;
  }
  return android || ios;
}

function toNativeIosAppStoreUrl(storeUrl) {
  const raw = String(storeUrl || "").trim();
  if (!raw) {
    return "";
  }

  if (/^itms-apps?:\/\//i.test(raw)) {
    return raw;
  }

  try {
    const parsed = new URL(raw);
    if (/^(apps|itunes)\.apple\.com$/i.test(parsed.hostname)) {
      return `itms-apps://${parsed.host}${parsed.pathname}${parsed.search}${parsed.hash}`;
    }
  } catch (error) {
    // fall through to id parse
  }

  const idMatch = raw.match(/\/id(\d+)/i) || raw.match(/\bid(\d+)\b/i);
  if (idMatch) {
    return `itms-apps://apps.apple.com/app/id${idMatch[1]}`;
  }

  return raw;
}

function isHttpUrl(url) {
  return /^https?:\/\//i.test(String(url || "").trim());
}

function getIosUniversalLink() {
  const raw = String(HM_Variables?.danhy_mobile_deeplink || "").trim();
  if (isHttpUrl(raw)) {
    return raw;
  }
  return "https://danhy-app-uat.hoanmy.com/";
}

function getAndroidCustomScheme() {
  const raw = String(HM_Variables?.danhy_mobile_deeplink_android || "").trim();
  if (raw && !isHttpUrl(raw)) {
    return raw;
  }
  return "hoanmyapp://openApp";
}

function parseAndroidPackageFromStoreUrl(storeUrl) {
  const value = String(storeUrl || "").trim();
  if (!value) {
    return "";
  }
  try {
    const parsed = new URL(value);
    return String(parsed.searchParams.get("id") || "").trim();
  } catch (error) {
    const match = value.match(/[?&]id=([a-zA-Z0-9._]+)/);
    return match ? match[1] : "";
  }
}

function getAndroidPackageName(storeUrl = "") {
  return (
    String(HM_Variables?.danhy_mobile_android_package || "").trim() ||
    parseAndroidPackageFromStoreUrl(storeUrl)
  );
}

/**
 * Native Play Store (market:// / Play Store intent), not play.google.com in Chrome.
 */
function toNativeAndroidPlayStoreUrl(storeUrl = "") {
  const raw = String(storeUrl || "").trim();
  if (/^market:\/\//i.test(raw)) {
    return raw;
  }

  const packageName = getAndroidPackageName(raw);
  if (!packageName) {
    return raw;
  }

  return `intent://details?id=${encodeURIComponent(packageName)}#Intent;scheme=market;package=com.android.vending;end`;
}

function buildAndroidIntentUrl(deeplink, storeUrl = "") {
  const raw = String(deeplink || "").trim();
  if (!raw || isHttpUrl(raw)) {
    return "";
  }

  let scheme = "hoanmyapp";
  let path = "openApp";
  try {
    const parsed = new URL(raw);
    scheme =
      String(parsed.protocol || "")
        .replace(":", "")
        .trim() || scheme;
    const host = String(parsed.hostname || parsed.host || "").trim();
    const pathname = String(parsed.pathname || "").replace(/^\//, "");
    path = host + (pathname ? `/${pathname}` : "");
    if (parsed.search) {
      path += parsed.search;
    }
  } catch (error) {
    const match = raw.match(/^([^:]+):\/\/(.*)$/);
    if (match) {
      scheme = match[1];
      path = match[2];
    }
  }

  const packageName = getAndroidPackageName(storeUrl);

  // package= without S.browser_fallback_url: Chrome opens the app, or native Play Store.
  // HTTPS fallback_url would load play.google.com in the browser instead.
  let intent = `intent://${path}#Intent;scheme=${scheme}`;
  if (packageName) {
    intent += `;package=${packageName}`;
  } else if (storeUrl) {
    intent += `;S.browser_fallback_url=${encodeURIComponent(storeUrl)}`;
  }
  intent += ";end";
  return intent;
}

function clearDanhyDeeplinkFallbackWatch() {
  if (fallbackTimer) {
    window.clearTimeout(fallbackTimer);
    fallbackTimer = null;
  }
  if (visibilityHandler) {
    document.removeEventListener("visibilitychange", visibilityHandler);
    visibilityHandler = null;
  }
  if (pageHideHandler) {
    window.removeEventListener("pagehide", pageHideHandler);
    pageHideHandler = null;
  }
}

function openDanhyStoreFallback() {
  const storeUrl = getDanhyStoreUrl();
  if (!storeUrl) {
    return;
  }

  const platform = getDanhyPlatform();

  if (platform === "android") {
    window.location.assign(toNativeAndroidPlayStoreUrl(storeUrl) || storeUrl);
    return;
  }

  if (platform === "ios") {
    window.location.assign(toNativeIosAppStoreUrl(storeUrl) || storeUrl);
    return;
  }

  window.location.assign(storeUrl);
}

/**
 * Try the installed Danh Y app, then fall back to the OS store.
 * Android: intent:// + package= (Chrome opens native Play Store if missing).
 * iOS: HTTPS Universal Link, then native App Store if the page never hides.
 */
function openDanhyAppDeeplink({ onBusyChange } = {}) {
  const platform = getDanhyPlatform();
  const storeUrl = getDanhyStoreUrl();
  const isAndroid = platform === "android";
  const launchUrl = isAndroid
    ? buildAndroidIntentUrl(getAndroidCustomScheme(), storeUrl) ||
      getAndroidCustomScheme()
    : getIosUniversalLink();

  if (!launchUrl) {
    openDanhyStoreFallback();
    return;
  }

  const setBusy = (busy) => {
    if (typeof onBusyChange === "function") {
      onBusyChange(busy);
    }
  };

  clearDanhyDeeplinkFallbackWatch();
  appOpened = false;
  setBusy(true);

  const finishBusy = () => setBusy(false);

  try {
    window.location.assign(launchUrl);
  } catch (error) {
    console.error("danhy deeplink failed", error);
    finishBusy();
    openDanhyStoreFallback();
    return;
  }

  // Android Intent already sends missing-app users to Play Store. A JS timeout
  // would still fire after a successful app switch (Chrome often stays "visible").
  if (isAndroid) {
    window.setTimeout(finishBusy, 800);
    return;
  }

  const markOpened = () => {
    if (document.hidden || document.visibilityState === "hidden") {
      appOpened = true;
      clearDanhyDeeplinkFallbackWatch();
      finishBusy();
    }
  };

  visibilityHandler = () => markOpened();
  pageHideHandler = () => {
    appOpened = true;
    clearDanhyDeeplinkFallbackWatch();
    finishBusy();
  };

  document.addEventListener("visibilitychange", visibilityHandler);
  window.addEventListener("pagehide", pageHideHandler);

  fallbackTimer = window.setTimeout(() => {
    fallbackTimer = null;
    clearDanhyDeeplinkFallbackWatch();
    finishBusy();

    if (appOpened || document.hidden || document.visibilityState === "hidden") {
      return;
    }

    openDanhyStoreFallback();
  }, 2000);
}

export {
  getDanhyStoreUrl,
  openDanhyAppDeeplink,
  openDanhyStoreFallback,
};
